import { ref, computed, watch, toRaw, nextTick, unref, onMounted, onBeforeUnmount } from 'vue'
import { map, union, castArray, isEmpty } from 'lodash'
import { UPDATE_MODEL_EVENT } from '@sgui-plus/utils/constants'
import { getNodesByKey } from './util'
import { SgTooltip } from '@sgui-plus/components/tooltip'
import { SgTree } from '@sgui-plus/components/tree'
import { SgInput } from '@sgui-plus/components/input'
import SgMultipleTags from '@sgui-plus/components/select/src/multiple-tags.vue'
import { useSize } from '@sgui-plus/hooks/use-common-props'
import { isNumber } from '@sgui-plus/utils/util'
import { addResizeListener, removeResizeListener } from '@sgui-plus/utils/resize-event'
import { useFormItem } from '@sgui-plus/hooks/use-form-item'

export const useComboTree = (props, emits?) => {
  // Ref
  const combotreeRef = ref<HTMLElement | null>(null)
  const tagsRef = ref<HTMLInputElement | null>(null)
  const multipleTagsRef = ref<InstanceType<typeof SgMultipleTags> | null>(null)
  const inputRef = ref<HTMLInputElement | null>(null)
  const reference = ref<InstanceType<typeof SgInput> | null>(null)
  const treeRef = ref<InstanceType<typeof SgTree> | null>(null)
  const tooltipRef = ref<InstanceType<typeof SgTooltip> | null>(null)

  // Data
  const visible = ref(false)
  const inputHovering = ref(false)
  const selected = ref<any>([])
  const selectedLabel = ref('')
  const inputLength = ref(20)
  const inputWidth = ref(0)
  const initialInputHeight = ref(0)

  // Inject
  const { form } = useFormItem()

  const size = useSize()

  // Computed
  const treeNodekey = computed(() => props.nodeKey || props.props.value || 'value')

  const tagsStyle = computed(() => ({
    maxWidth: `${unref(inputWidth) - 32}px`,
    width: '100%',
    height: props.fixedHeight ? '28px' : '',
    overflow: props.fixedHeight ? 'auto' : ''
  }))

  const dropMenuWidth = computed(() => {
    const width = isNumber(props.dropWidth) ? `${props.dropWidth}px` : props.dropWidth
    return  width || inputWidth.value + 'px'
  })

  const dropMenuHeight = computed(() =>
    isNumber(props.dropHeight) ? `${props.dropHeight}px` : props.dropHeight
  )

  const dropMenuVisible = computed({
    get() {
      return visible.value && isEditable.value
    },
    set(val: boolean) {
      visible.value = val
    }
  })

  const isDisabled = computed(() => props.disabled || form?.disabled)

  const isEditable = computed(() => !isDisabled.value && !props.readonly)

  const showClose = computed(() => {
    const hasValue = props.multiple
      ? Array.isArray(props.modelValue) && props.modelValue.length > 0
      : props.modelValue !== undefined &&
        props.modelValue !== null &&
        props.modelValue !== ''

    const criteria =
      props.clearable &&
      isEditable.value &&
      inputHovering.value &&
      hasValue

    return criteria
  })

  // 更新value值
  const updateValue = computed({
    get() {
      return props.modelValue
    },
    set(val) {
      emits(UPDATE_MODEL_EVENT, val)
    }
  })

  /**
   * 关闭面板
   */
  const handleClose = () => {
    visible.value = false
  }

  /**
   * 打开面板
   */
  const handleOpen = () => {
    visible.value = true
  }

  const handleToggle = () => {
    if (visible.value) {
      handleClose()
    } else {
      handleOpen()
    }
  }

  /**
   * 设置展示值
   */
  const setSelectedLabel = () => {
    const modelValues = props.modelValue ? castArray(toRaw(props.modelValue)) : []
    const treeNodes = getNodesByKey(props.data, modelValues, treeNodekey.value)

    if (props.multiple) {
      selected.value = modelValues.reduce((res, value) => {
        const node = treeNodes.find(item => item[treeNodekey.value] === value)

        if (node) {
          res.push(toRaw(node))
        } else if (props.allowCreate) {
          // 格式化手动输入的内容，以便于展示
          res.push({ [props.props.label]: value, [treeNodekey.value]: value })
        }

        return res
      }, [])
    } else {
      const currentNode = toRaw(treeNodes[0])
      selectedLabel.value = currentNode ? currentNode[props.props.label] : props.allowCreate ? props.modelValue : ''
    }
  }

  // 删除标签
  const deleteTag = (tag: string) => {
    const index = selected.value.indexOf(tag)
    if (index > -1 && isEditable.value) {
      const value = props.modelValue.slice()
      const deletedKeys = value.splice(index, 1)
      setValue(value)
      resetInputHeight()
      treeRef.value?.setCheckedNodes(deletedKeys, false)
      emits('onRemove', tag)
    }
  }

  // 创建标签
  const createTag = (value) => {
    if (props.allowCreate) {
      // 运行手动创建标签
      if (props.multiple) {
        setValue([...props.modelValue, value])
        // 重置单选的输入内容
        selectedLabel.value = ''
        // 重置输入内容
        inputRef.value!.value = ''
      } else {
        setValue(value)
      }
      treeRef.value?.setChecked(value)
    }
  }

  // 清空选中数据
  const clearSelected = (e) => {
    e.stopPropagation()
    setValue('')
    emits('onClear')
  }

  // 重置输入框宽度
  const resetInputWidth = () => {
    inputWidth.value = reference.value?.$el.getBoundingClientRect().width
  }

  const resetInputState = (event) => {
    inputLength.value = inputRef.value!.value.length * 15 + 20
    resetInputHeight()
  }

  // 重置输入框高度
  const resetInputHeight = () => {
    // 多标签省略不需要调整高度
    if ((props.collapseTags || props.ellipsisTag)) {
      return
    }

    nextTick(() => {
      if (!reference.value) {
        return
      }

      const inputChildNodes = reference.value.$el.childNodes
      const input = Array.from(inputChildNodes).filter(
        (item) => (item as HTMLElement).tagName === 'INPUT'
      )[0] as HTMLInputElement
      const sizeInMap = initialInputHeight.value || 40
      input.style.height =
        selected.value.length === 0
          ? `${sizeInMap}px`
          : `${Math.max(
              tagsRef.value
                ? tagsRef.value.clientHeight + (tagsRef.value.clientHeight > sizeInMap ? 6 : 0)
                : 0,
              sizeInMap
            )}px`

      if (visible.value) {
        tooltipRef.value?.updatePopper?.()
      }
    })
  }

  const handleResize = () => {
    if (props.multiple) {
      multipleTagsRef.value?.calculateTags(props.modelValue)
    }
    resetInputWidth()
    resetInputHeight()
    tooltipRef.value?.updatePopper?.()
  }

  const handleNodeClick = (node) => {
    // 只选择子节点，点击父节点时进行展开
    if (props.justChildNodes && node.isParent) {
      treeRef.value?.expandNode(node[treeNodekey.value])
      return
    }

    // 只选择父节点，点击自己点时不进行响应
    if (props.justParentNodes && !node.isParent) {
      return
    }

    if (!props.multiple) {
      setValue(node[treeNodekey.value])
    }

    emits('onNodeClick', node)
  }

  const handleCheckChange = (node, checked) => {
    let checkedNodes = treeRef.value?.getCheckedNodes()

    // 只选择父节点
    if (props.justParentNodes) {
      checkedNodes = checkedNodes.filter((item) => !checkedNodes.some(o => o.tId === item.parentTId))
    }

    const modelValues = props.modelValue ? castArray(toRaw(props.modelValue)) : []
    const retainValues = modelValues.filter(value => {
      const node = treeRef.value?.getNode(value)
      // 手动创建项和不显示的选中项仍需保留
      return isEmpty(node) || node.isHidden
    })

    const checkedValues = union(map(checkedNodes, treeNodekey.value), retainValues)

    setValue(checkedValues)
    emits('onCheckChange', node, checked)
  }

  const handleFocus = () => {
    const _input = inputRef.value || reference.value
    if (_input) {
      _input?.focus()
    }
  }

  const setValue = (value) => {
    updateValue.value = value
    // 值更改时需更新popperpane的位置
    tooltipRef.value?.updatePopper?.()
  }

  // 数据控制值
  watch(
    () => props.modelValue,
    (val) => {
      emits('onChange', val)

      setSelectedLabel()
      handleResize()
      if (!props.multiple) {
        handleClose()
      }
    },
    {
      flush: 'post',
      deep: true
    }
  )

  watch(
    () => visible.value,
    (val) => {
      emits('onVisibleChange', val)
    }
  )

  onMounted(() => {
    addResizeListener(combotreeRef.value as any, handleResize)

    if (reference.value && reference.value.$el) {
      const sizeMap = {
        large: 36,
        default: 32,
        small: 28,
      }
      const input = reference.value.input as HTMLInputElement
      initialInputHeight.value = input.getBoundingClientRect().height || sizeMap[size.value]
    }

    setSelectedLabel()
  })

  onBeforeUnmount(() => {
    removeResizeListener(combotreeRef.value as any, handleResize)
  })

  return {
    combotreeRef,
    tagsRef,
    inputRef,
    reference,
    treeRef,
    tooltipRef,

    visible,
    selected,
    selectedLabel,
    isDisabled,
    isEditable,
    tagsStyle,
    showClose,
    inputHovering,
    inputLength,
    inputWidth,
    dropMenuVisible,
    dropMenuWidth,
    dropMenuHeight,
    treeNodekey,
    handleClose,
    handleToggle,
    handleNodeClick,
    handleCheckChange,
    handleFocus,
    deleteTag,
    createTag,
    clearSelected,
    resetInputState,
    resetInputHeight
  }
}
